import { faCircleDownRight } from "@fortawesome/pro-regular-svg-icons/faCircleDownRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Stack, Tab, Typography } from "@mui/material";
import { AppImages } from "app/appImages";
import { MprcLink } from "components";
import { MprcChipTabs } from "components/MprcChipTabs";
import { FeedTopicHeaderResponse, TopicSelectorMode, UserItemDtoPaginatedList } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TopicSelectorHierarchicalDrawer } from "views/foundation/topic/drawers/TopicSelectorHierarchicalDrawer";
import { ListUsersDrawer } from "views/foundation/user/drawers/ListUsersDrawer";
import { ComplexInfoHeader } from "views/knowledge/components/ComplexInfoHeader";
import { useFeedTopicContext } from "views/knowledge/feed/FeedTopic";
import { ActionTopicButton, ShareTopicButton, SubscribeTopicButton, ToggleMutedTopicButton, UnsubscribeTopicButton } from "views/knowledge/feed/FeedTopic/defaults";

interface IProps {
    onTopicChange: (topicId: string) => void;
    onDescendants: (includeDescendants: boolean) => void;
    onReset: (newTopic?: FeedTopicHeaderResponse) => void;
    onRigthPanelButtonClick?: () => void;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Cabeçalho do Feed de Tópicos
 * @param props
 */
export const TopicHeader: React.FC<IProps> = (props) => {

    const { topic, includeDescendants, isHome } = useFeedTopicContext();
    const { onTopicChange, onDescendants, onReset, onRigthPanelButtonClick } = props;

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const { mode, setMode } = useFeedTopicContext();

    const [openDrawer, setOpenDrawer] = useState<boolean>(false);
    const [openMembersDrawer, setOpenMembersDrawer] = useState<boolean>(false);

    const subtitle = useMemo(() => {
        const items: React.ReactNode[] = [];
        items.push(t("shared:SysObjects.Topic"));
        items.push(
            <MprcLink onClick={() => setOpenMembersDrawer(true)}>
                <Typography variant="caption" color="white" fontWeight="bold">
                    {t("Topic.TotalSubscribersText", { count: topic.subscriptionCount })}
                </Typography>
            </MprcLink>
        );

        return items;
    }, [topic.subscriptionCount]);

    ///
    const title = () => {
        return <Stack direction="row" spacing={2} alignItems="baseline" width="100%">
            <MprcLink onClick={() => setOpenDrawer(true)} title={t("Topic.SelectOtherTopic")} noWrap>
                <Typography variant="h1" color="white" noWrap>
                    {topic.name}
                </Typography>
            </MprcLink>
            {topic.hasChildren &&
                <MprcLink onClick={() => onDescendants(!includeDescendants)} title={t("Topic.IncludeDescendants")} noWrap minWidth="22px">
                    <Typography color={includeDescendants ? "warning.main" : "muted.main"} noWrap>
                        <FontAwesomeIcon icon={faCircleDownRight} size="lg" />
                    </Typography>
                </MprcLink>
            }
        </Stack>;
    };

    const subscribersDrawer =
        openMembersDrawer && <ListUsersDrawer
            title={t("Feed.Channel.SubscribersDrawerTitle")}
            subtitle={topic.name}
            onGetData={({ searchText, pageNumber, pageSize }) =>
                new Promise<UserItemDtoPaginatedList>((resolve, reject) => {
                    api.v1.topic.getUsers({
                        topicId: topic.topicId,
                        searchText: searchText,
                        pageNumber: pageNumber,
                        pageSize: pageSize
                    })
                        .then(({ data }) => resolve(data))
                        .catch(e => reject(e));
                })}
            onClose={() => setOpenMembersDrawer(false)}
        />;
    return (
        <>
            {subscribersDrawer}
            {
                openDrawer &&
                <TopicSelectorHierarchicalDrawer
                    mode={TopicSelectorMode.Normal}
                    onClose={(topic) => {
                        if (topic && topic.length > 0) {
                            onTopicChange(topic[0].topicId);
                        }
                        setOpenDrawer(false);
                    }}
                />
            }
            <ComplexInfoHeader
                title={title()}
                subtitle={subtitle}
                description={topic.description}
                thumbnailUrl={topic.thumbnailUrl}
                disableGoback={isHome}
                defaultThumbnail={AppImages.TopicThumbnail}
                onRigthPanelButtonClick={onRigthPanelButtonClick}
                filters={
                    <MprcChipTabs
                        value={mode}
                        onChange={(e, value) => setMode(value)}
                    >
                        <Tab
                            label={t("Topic.GotoKnowledgeTitle")}
                            title={t("Topic.GotoKnowledgeTooltip")}
                            value="knowledge"
                        />
                        <Tab
                            label={t("Topic.GotoTrainingsTitle")}
                            title={t("Topic.GotoTrainingsTooltip")}
                            value="lms"
                        />
                    </MprcChipTabs>
                }
                actions={
                    <Grid container alignItems="center">
                        <Grid>
                            <ShareTopicButton topic={topic} />
                        </Grid>
                        <Grid>
                            <ActionTopicButton topic={topic} />
                        </Grid>
                    </Grid>
                }
                buttons={
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={1}
                        width="100%"
                        sx={{ pr: 2, pl: 2 }}
                    >
                        {
                            topic.registered
                                ? (
                                    <>
                                        <UnsubscribeTopicButton
                                            topicId={topic.topicId}
                                            required={topic.registered && topic.required}
                                            isDirectUser={topic.isDirectUser}
                                            onRefresh={() => onReset()}
                                        />
                                        <ToggleMutedTopicButton
                                            topic={topic}
                                            onSuccess={() => onReset({ ...topic, muted: !topic!.muted })}
                                        />
                                    </>
                                )
                                : topic.allowUserAutoInterest &&
                                <SubscribeTopicButton
                                    topicId={topic.topicId}
                                    required={topic.required}
                                    isDirectUser={topic.isDirectUser}
                                    onRefresh={() => onReset()}
                                />

                        }
                    </Stack>
                }
            />
        </>
    );
};
